import React from 'react';
import { Calendar, RefreshCw } from 'lucide-react';
import { HeatmapFilterState } from './types';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface HeatmapFilterPanelProps {
    filterState: HeatmapFilterState;
    onFilterChange: (state: HeatmapFilterState) => void;
    onReset: () => void;
    onApply: () => void;
    isLoading: boolean;
}

export const HeatmapFilterPanel: React.FC<HeatmapFilterPanelProps> = ({
    filterState,
    onFilterChange,
    onReset,
    onApply,
    isLoading
}) => {
    const { t } = useTranslation();

    const handleChange = (field: keyof HeatmapFilterState, value: any) => {
        onFilterChange({
            ...filterState,
            [field]: value
        });
    };

    return (
        <div className="space-y-4">
            <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                    <Calendar className="w-3 h-3 inline mr-1" />
                    {t('manager.modules.locationHeatmap.filter.startDate', {}, 'Startdato')}
                </label>
                <input
                    type="date"
                    value={filterState.startDate}
                    onChange={(e) => handleChange('startDate', e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
            </div>

            <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                    <Calendar className="w-3 h-3 inline mr-1" />
                    {t('manager.modules.locationHeatmap.filter.endDate', {}, 'Sluttdato')}
                </label>
                <input
                    type="date"
                    value={filterState.endDate}
                    onChange={(e) => handleChange('endDate', e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
            </div>

            <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                    {t('manager.modules.locationHeatmap.filter.transType', {}, 'Transaksjonstype')}
                </label>
                <select
                    value={filterState.transType || ''}
                    onChange={(e) => handleChange('transType', e.target.value ? parseInt(e.target.value) : null)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                    <option value="">{t('manager.modules.locationHeatmap.filter.transTypeAll', {}, 'Alle')}</option>
                    <option value="10">{t('manager.modules.locationHeatmap.filter.transType10', {}, '10 - Plukk')}</option>
                    <option value="20">{t('manager.modules.locationHeatmap.filter.transType20', {}, '20 - Plassering')}</option>
                    <option value="30">{t('manager.modules.locationHeatmap.filter.transType30', {}, '30 - Flytting')}</option>
                    <option value="40">{t('manager.modules.locationHeatmap.filter.transType40', {}, '40 - Telling')}</option>
                    <option value="50">{t('manager.modules.locationHeatmap.filter.transType50', {}, '50 - Korreksjon')}</option>
                </select>
            </div>

            <div className="space-y-2">
                <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={filterState.includeWeekends}
                        onChange={(e) => handleChange('includeWeekends', e.target.checked)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{t('manager.modules.locationHeatmap.filter.includeWeekends', {}, 'Inkluder helger')}</span>
                </label>

                <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={filterState.includeHolidays}
                        onChange={(e) => handleChange('includeHolidays', e.target.checked)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{t('manager.modules.locationHeatmap.filter.includeHolidays', {}, 'Inkluder helligdager')}</span>
                </label>
            </div>

            <div className="pt-3 border-t border-gray-200 space-y-2">
                <button
                    onClick={onApply}
                    disabled={isLoading}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isLoading ? (
                        <>
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            <span>{t('common.status.loading', {}, 'Laster...')}</span>
                        </>
                    ) : (
                        <>
                            <RefreshCw className="w-4 h-4" />
                            <span>{t('manager.modules.locationHeatmap.filter.fetchHeatmap', {}, 'Hent heatmap')}</span>
                        </>
                    )}
                </button>

                <button
                    onClick={onReset}
                    disabled={isLoading}
                    className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                    {t('manager.modules.locationHeatmap.filter.resetFilter', {}, 'Nullstill filter')}
                </button>
            </div>
        </div>
    );
};
